import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiStar } from 'react-icons/fi';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/hooks/useLanguage';
import { AppStoreDownloadDialog } from '@/components/common/AppStoreDownloadDialog';
import { ROUTE_PATHS } from '@/routes/routePaths';



interface HeroSectionProps {
  onQuickRideClick: () => void;
  showAuthButtons?: boolean;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onQuickRideClick, showAuthButtons = true }) => {
  const { t } = useLanguage();
  const [showDialog, setShowDialog] = React.useState(false);
  const stats = [
    { value: t('cityToCityRides', 'hero'), label: t('rideBetweenCities', 'hero') },
    { value: t('shareCost', 'hero'), label: t('shareToSave', 'hero') },
    { value: t('perSeatBooking', 'hero'), label: t('reserveYourSeat', 'hero') },
    { value: t('privateVehicles', 'hero'), label: t('privateComfortable', 'hero') },
    {
      value: t('dailyCommuteCarpool', 'hero'),
      label: t('dailyCommuteCarpool', 'hero'),
      href: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL,
    },
  ];

  return (
    <section id="hero" className="relative overflow-hidden min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-gray-50 to-blue-50" style={{ scrollMarginTop: '100px' }}>
      {/* Background Image */}
      <div className="absolute inset-0 flex items-center justify-center opacity-20">
        <img
          src="/Chalo-uploads/ed3e4cb1-8d78-4271-9f34-a0f4c209dca5.png"
          alt="Happy travelers in car sharing ride"
          className="w-full max-w-8xl h-auto object-contain"
          decoding="async"
          fetchpriority="low"
          loading="lazy"
          width={1200}
          height={800}
        />
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Moving geometric shapes */}
        <div className="absolute top-1/4 left-10 w-32 h-32 bg-gradient-to-br from-[#22C55E]/20 to-transparent rounded-full blur-xl animate-float-slow"></div>
        <div className="absolute top-1/3 right-20 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-transparent rounded-full blur-lg animate-float-reverse"></div>
        <div className="absolute bottom-1/3 left-1/4 w-40 h-40 bg-gradient-to-br from-green-400/15 to-transparent rounded-full blur-2xl animate-pulse-slow"></div>
        
        {/* Animated lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22C55E" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#22C55E" stopOpacity="0"/>
            </linearGradient>
          </defs>
          <path d="M0,300 Q400,100 800,250 T1600,200" stroke="url(#lineGradient)" strokeWidth="2" fill="none" className="animate-draw-line"/>
          <path d="M0,500 Q600,300 1200,400 T2400,350" stroke="url(#lineGradient)" strokeWidth="1.5" fill="none" className="animate-draw-line-delayed"/>
        </svg>
      </div>

      {/* Glass morphism container */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden py-8">
        <div className="text-center">
          {/* Glass badge */}
          <div className="inline-flex items-center space-x-2 bg-white/40 backdrop-blur-md border border-white/30 px-6 py-3 rounded-full mb-8 shadow-lg animate-fade-in-up">
            <FiStar className="w-4 h-4 text-[#22C55E] animate-pulse" />
            <span className="text-sm font-medium text-gray-700">
              {t('badge', 'hero')}
            </span>
          </div>

          {/* Main Heading with enhanced glass effect */}
          <div className="relative mb-8">
            <h1
              className="w-full max-w-full text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-light text-gray-900 mb-4 leading-tight animate-slide-up-stagger px-2 sm:px-4 break-words overflow-wrap break-word"
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word', hyphens: 'auto', maxWidth: '100%', padding: '0.25em 0', margin: 0 }}
            >
              <span className="block opacity-90 break-words w-full" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', hyphens: 'auto', maxWidth: '100%' }}>{t('mainTitle', 'hero')}</span>
              <span
                className="block font-medium text-[#22C55E] bg-gradient-to-r from-[#22C55E] to-orange-600 bg-clip-text text-transparent animate-text-glow break-words w-full"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                  maxWidth: '100%',
                  lineHeight: 1.15,
                  padding: '0.25em 0',
                  margin: 0,
                  display: 'block',
                }}
              >
                {t('mainTitleHighlight', 'hero')}
              </span>
            </h1>
            
            {/* Glass reflection effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-transparent via-white/5 to-transparent blur-xl opacity-50 animate-shimmer"></div>
          </div>

          {/* Subtitle with enhanced glass container */}
          <div className="bg-white/30 backdrop-blur-sm border border-white/40 rounded-3xl p-8 mb-12 shadow-xl animate-fade-in-up-delayed">
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
              {t('subtitle', 'hero')}
            </p>
          </div>

          {/* CTA Buttons with enhanced glass effect */}
          {showAuthButtons && (
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16 animate-fade-in-up-delayed-2">
              <Button
                size="lg"
                asChild
                className="group relative bg-[#22C55E]/90 backdrop-blur-md hover:bg-[#22C55E] text-white text-lg px-12 py-6 h-auto rounded-full font-medium shadow-2xl hover:shadow-[#22C55E]/25 transition-all duration-500 hover:scale-105 border border-[#22C55E]/20"
              >
                <Link to={ROUTE_PATHS.FIND_RIDE}>
                  <span className="flex items-center space-x-3">
                    <span>{t('findRide', 'hero')}</span>
                    <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Link>
              </Button>
              
              <Button 
                size="lg"
                variant="outline"
                onClick={() => setShowDialog(true)}
                className="group bg-white/40 backdrop-blur-md text-lg px-12 py-6 h-auto rounded-full border-2 border-white/50 text-gray-700 hover:bg-white/50 font-medium transition-all duration-500 hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                <span className="flex items-center space-x-3">
                  <span>{t('offerRide', 'hero')}</span>
                </span>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </Button>
            </div>
          )}

          {/* Stats with enhanced glass cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 max-w-6xl mx-auto w-full animate-fade-in-up-delayed-3">
            {stats.map((stat, index) => {
              const cardClassName =
                'group inline-flex flex-col items-center w-full relative bg-white/35 backdrop-blur-md border border-white/40 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105 hover:bg-white/45 text-center';

              const cardContent = (
                <>
                  <div className="text-lg sm:text-xl font-light text-gray-900 mb-2 group-hover:text-[#22C55E] transition-colors duration-300">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    {stat.label}
                  </div>
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </>
              );

              if (stat.href) {
                return (
                  <Link key={index} to={stat.href} className={cardClassName}>
                    {cardContent}
                  </Link>
                );
              }

              return (
                <div key={index} className={cardClassName}>
                  {cardContent}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Floating elements */}
      <div className="absolute top-20 right-10 w-4 h-4 bg-[#22C55E] rounded-full animate-bounce-slow opacity-60"></div>
      <div className="absolute bottom-32 left-20 w-3 h-3 bg-blue-400 rounded-full animate-bounce-delayed opacity-50"></div>
      <div className="absolute top-1/2 left-10 w-2 h-2 bg-green-400 rounded-full animate-pulse opacity-40"></div>

      {/* App Store Download Dialog */}
      <AppStoreDownloadDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        title="Download our app to book or host rides"
      />
    </section>
  );
};
